/**
 * Single source of truth for topic cluster metadata (charts, table, drawer).
 *
 * Axis scores for *fallback* are signed "political compass"-style metrics:
 * - internalExternal: -100 = Internal, +100 = External, 0 = balanced
 * - operationalStrategic: -100 = Strategic, +100 = Operational, 0 = balanced
 *
 * Once the backend starts returning real per-topic axis values, the UI should
 * use those topic-level numbers. These centers are only used as a fallback
 * for older reports missing axis metrics.
 */

export const TOPIC_CLUSTERS = [
  {
    numericId: 1,
    id: 'productDevelopment',
    name: 'Product & Development',
    color: '#10B981',
    description: 'Product development, technical features, and roadmap planning',
    keywords: [
      'feature',
      'development',
      'code',
      'api',
      'tech',
      'design',
      'build',
      'release',
      'product',
      'roadmap'
    ],
    // internalExternal 25/100 => -50 signed; operationalStrategic 75/100 => +50 signed
    center: { internalExternal: -50, operationalStrategic: 50 }
  },
  {
    numericId: 2,
    id: 'customerSupport',
    name: 'Customer & Support',
    color: '#3B82F6',
    description: 'Customer interactions, support, and user experience',
    keywords: [
      'user',
      'customer',
      'support',
      'feedback',
      'issue',
      'request',
      'help',
      'satisfaction',
      'experience'
    ],
    // 75/100 => +50; 35/100 => -30
    center: { internalExternal: 50, operationalStrategic: -30 }
  },
  {
    numericId: 3,
    id: 'operationsProcess',
    name: 'Operations & Process',
    color: '#EF4444',
    description: 'Team processes, workflows, and internal coordination',
    keywords: [
      'meeting',
      'process',
      'workflow',
      'team',
      'standup',
      'sprint',
      'review',
      'coordination',
      'internal'
    ],
    // 25/100 => -50; 35/100 => -30
    center: { internalExternal: -50, operationalStrategic: -30 }
  },
  {
    numericId: 4,
    id: 'businessStrategy',
    name: 'Business & Strategy',
    color: '#F59E0B',
    description: 'Business strategy, market analysis, and growth initiatives',
    keywords: [
      'strategy',
      'business',
      'market',
      'growth',
      'revenue',
      'goals',
      'metrics',
      'analytics',
      'performance'
    ],
    // 75/100 => +50; 75/100 => +50
    center: { internalExternal: 50, operationalStrategic: 50 }
  },
  {
    numericId: 5,
    id: 'communicationCulture',
    name: 'Communication & Culture',
    color: '#8B5CF6',
    description: 'General communication, announcements, and team culture',
    keywords: [
      'announcement',
      'communication',
      'culture',
      'social',
      'celebration',
      'update',
      'general',
      'random'
    ],
    // 50/100 => 0; 20/100 => -60
    center: { internalExternal: 0, operationalStrategic: -60 }
  }
];

const byBackendId = Object.fromEntries(TOPIC_CLUSTERS.map((c) => [c.id, c]));
const byNumericId = Object.fromEntries(TOPIC_CLUSTERS.map((c) => [c.numericId, c]));
const byDisplayName = Object.fromEntries(TOPIC_CLUSTERS.map((c) => [c.name, c]));

export function getClusterByBackendId(backendId) {
  if (!backendId) return null;
  return byBackendId[backendId] || null;
}

export function getClusterByNumericId(numericId) {
  if (numericId == null) return null;
  return byNumericId[numericId] || null;
}

export function getClusterByDisplayName(name) {
  if (!name) return null;
  return byDisplayName[name] || null;
}

export function getAxisScoresForCluster(backendId) {
  const c = getClusterByBackendId(backendId);
  if (!c) return { internalExternal: 0, operationalStrategic: 0 };
  return { ...c.center };
}
